import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "react-oidc-context";
import { getActionTypeInfo } from "./utils/actionTypeUtils";

const API_URL = process.env.REACT_APP_API_URL || "/api";
const IMAGE_SERVICE_URL = process.env.REACT_APP_IMAGE_SERVICE_URL || "/api-images";
const IMAGE_API_TOKEN = process.env.REACT_APP_IMAGE_API_TOKEN || "";
const isLocalDev = window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1";
const CDN_LOCAL_BASE = "http://localhost:8090/images";
const CDN_STAGING_BASE = "https://cdn-staging.luniwave.com/images";
const CDN_PROD_BASE = "https://cdn.luniwave.com/images";

const getMappedCdnBaseUrl = () => {
  const host = window.location.hostname.toLowerCase();
  if (host === "localhost" || host === "127.0.0.1") return CDN_LOCAL_BASE;
  if (host.includes("preprod") || host.includes("staging")) return CDN_STAGING_BASE;
  return CDN_PROD_BASE;
};

const extractFilenameFromUrl = (imageUrl) => {
  if (!imageUrl) return "";
  try {
    const parsedUrl = new URL(imageUrl, window.location.origin);
    const filename = parsedUrl.pathname.split("/").filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : "";
  } catch (err) {
    const filename = String(imageUrl).split("?")[0].split("/").filter(Boolean).pop();
    return filename ? decodeURIComponent(filename) : "";
  }
};

/**
 * Helper pour obtenir le nom d'une langue
 */
const getLanguageName = (code) => {
  const languageNames = {
    fr: "Français",
    en: "English",
    es: "Español",
    de: "Deutsch",
    it: "Italiano",
    pt: "Português",
    nl: "Nederlands",
    pl: "Polski",
    ru: "Русский",
    zh: "中文",
    ja: "日本語",
    ko: "한국어",
    ar: "العربية",
  };
  return languageNames[code] || code.toUpperCase();
};

export const validateActionFormData = (formData, currentActionType) => {
  const newErrors = {};

  // Validation des descriptions
  const frenchDesc = formData.descriptions.find((d) => d.language === "fr");
  if (!frenchDesc?.name?.trim()) {
    newErrors.descriptions = "Le titre en français est obligatoire";
  } else if (!frenchDesc?.subtitle?.trim()) {
    newErrors.descriptions = "Le sous-titre en français est obligatoire";
  } else if (!frenchDesc?.description?.trim()) {
    newErrors.descriptions = "La description en français est obligatoire";
  }

  if (!formData.iconUrl?.trim()) {
    newErrors.iconUrl = "L'URL de l'icône est obligatoire";
  } else {
    try {
      new URL(formData.iconUrl.trim());
    } catch (e) {
      newErrors.iconUrl = "L'URL de l'icône doit être valide";
    }
  }

  const formActionType = formData.type || currentActionType;
  const formTypeInfo = getActionTypeInfo(formActionType);

  // Validation de la question si nécessaire
  if (formTypeInfo?.requiresQuestion && formActionType !== "QUIZ") {
    if (!frenchDesc?.question?.trim()) {
      newErrors.question = "La question en français est obligatoire pour ce type d'action";
    }
  }

  // Validation de la réponse positive
  if (formTypeInfo?.requiresPositiveResponse && !formData.actPositiveResponse) {
    newErrors.actPositiveResponse = "La réponse positive est obligatoire";
  }

  // Validation de la récompense
  if (formData.actReward === "" || formData.actReward === null || formData.actReward === undefined) {
    newErrors.actReward = "La récompense est obligatoire";
  } else if (Number(formData.actReward) < 0) {
    newErrors.actReward = "La récompense doit être supérieure ou égale à 0";
  }

  // Validation LINK
  if (formActionType === "LINK") {
    if (!formData.actLinkUrl?.trim()) {
      newErrors.actLinkUrl = "L'URL du lien est obligatoire";
    } else {
      try {
        new URL(formData.actLinkUrl.trim());
      } catch (e) {
        newErrors.actLinkUrl = "L'URL doit être valide";
      }
    }
  }

  // Validation CUSTOM
  if (formActionType === "CUSTOM") {
    if (!formData.customOptions || formData.customOptions.length < 2) {
      newErrors.customOptions = "Au moins 2 options sont obligatoires";
    } else {
      formData.customOptions.forEach((option, idx) => {
        if (!option.descriptions || option.descriptions.length === 0) {
          newErrors[`customOption_${idx}`] = `L'option ${idx + 1} doit avoir au moins une description`;
        } else {
          option.descriptions.forEach((desc, descIdx) => {
            if (!desc.label?.trim()) {
              newErrors[`customOption_${idx}_desc_${descIdx}`] = `Le libellé est obligatoire pour l'option ${idx + 1}`;
            }
          });
        }
      });
    }
  }

  // Validation QUIZ
  if (formActionType === "QUIZ") {
    if (!formData.quizData) {
      newErrors.quizData = "Les données du quiz sont obligatoires";
    } else {
      if (!formData.quizData.descriptions || formData.quizData.descriptions.length === 0) {
        newErrors.quizDescriptions = "Au moins une description de question est obligatoire";
      } else {
        formData.quizData.descriptions.forEach((desc, idx) => {
          if (!desc.question?.trim()) {
            newErrors[`quizDesc_${idx}`] = "La question est obligatoire";
          }
        });
      }

      if (!formData.quizData.answers || formData.quizData.answers.length < 2) {
        newErrors.quizAnswers = "Au moins 2 réponses sont obligatoires";
      } else {
        const hasCorrect = formData.quizData.answers.some((a) => a.isCorrect === 1);
        if (!hasCorrect) {
          newErrors.quizAnswers = "Au moins une réponse correcte est obligatoire";
        }

        formData.quizData.answers.forEach((answer, ansIdx) => {
          if (!answer.descriptions || answer.descriptions.length === 0) {
            newErrors[`quizAnswer_${ansIdx}`] = `La réponse ${ansIdx + 1} doit avoir au moins une description`;
          } else {
            answer.descriptions.forEach((desc, descIdx) => {
              if (!desc.text?.trim()) {
                newErrors[`quizAnswer_${ansIdx}_desc_${descIdx}`] = `Le texte est obligatoire pour la réponse ${ansIdx + 1}`;
              }
            });
          }
        });
      }
    }
  }

  return newErrors;
};

/**
 * Formulaire simplifié en une seule page pour créer une action
 * Version 2 - Repart de zéro avec une approche simple et intuitive
 * Gère tous les types d'actions avec leurs spécificités
 */
function SimpleActionFormV2({ hotelId, actionType, actionId, onSuccess, onCancel }) {
  const auth = useAuth();
  const token = auth.user?.access_token;

  const isEditMode = !!actionId;
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditMode);
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [loadingLanguages, setLoadingLanguages] = useState(true);
  const [errors, setErrors] = useState({});
  const [detectedActionType, setDetectedActionType] = useState(actionType || null);
  const [cdnImages, setCdnImages] = useState([]);
  const [loadingCdnImages, setLoadingCdnImages] = useState(false);
  const [cdnImageSearch, setCdnImageSearch] = useState("");
  const [cdnUploadFile, setCdnUploadFile] = useState(null);
  const [cdnUploadName, setCdnUploadName] = useState("");
  const [uploadingToCdn, setUploadingToCdn] = useState(false);
  const [imageLibraryMessage, setImageLibraryMessage] = useState("");
  const [imageLibraryMessageType, setImageLibraryMessageType] = useState("info");
  const cdnTargetField = "iconUrl";

  // Utiliser detectedActionType si disponible, sinon actionType
  const currentActionType = detectedActionType || actionType;
  const typeInfo = getActionTypeInfo(currentActionType);

  // Fonction pour créer l'état initial du formulaire
  const createInitialFormData = (type) => {
    const typeInfoForType = getActionTypeInfo(type);
    return {
      // Informations de base
      actReward: 5,
      imageUrl: "",
      iconUrl: "",
      type: type,

      // Descriptions multilingues (FR et EN par défaut)
      descriptions: [
        {
          language: "fr",
          name: "",
          nameB: "",
          subtitle: "",
          description: "",
          ...(typeInfoForType?.requiresQuestion && type !== "QUIZ" ? { question: "" } : {}),
          ...(type === "LINK" ? { question: "", linkButtonText: "" } : {}),
        },
        {
          language: "en",
          name: "",
          nameB: "",
          subtitle: "",
          description: "",
          ...(typeInfoForType?.requiresQuestion && type !== "QUIZ" ? { question: "" } : {}),
          ...(type === "LINK" ? { question: "", linkButtonText: "" } : {}),
        },
      ],

      // Réponse positive (pour YESNO, YESNOSTAY, YESNOMAIL)
      actPositiveResponse: "YES",

      // Économies (à 0 par défaut)
      actSavingsEuro: 0,
      actSavingsWater: 0,
      actSavingsCO2: 0,

      // Options personnalisées (pour CUSTOM)
      customOptions: [],

      // Données quiz (pour QUIZ)
      quizData:
        type === "QUIZ"
          ? {
              difficulty: "EASY",
              descriptions: [
                { language: "fr", question: "", explanation: "" },
                { language: "en", question: "", explanation: "" },
              ],
              answers: [
                {
                  position: 1,
                  isCorrect: 1,
                  descriptions: [
                    { language: "fr", text: "" },
                    { language: "en", text: "" },
                  ],
                },
                {
                  position: 2,
                  isCorrect: 0,
                  descriptions: [
                    { language: "fr", text: "" },
                    { language: "en", text: "" },
                  ],
                },
              ],
            }
          : null,

      // URL du lien (pour LINK)
      actLinkUrl: "",

      // Config Option Eau (pour LUNISHOWER)
      actEauConnected: false,
      actEauObjectiveLiters: 40,
      actEauPointsPerShower: 100,

      // Sous-type de service (pour SKIP_SERVICE : cleaning ou sheets)
      skipSubType: "cleaning",

      // Groupe visuel sur l'app invité (POINTS = défaut, section "Gagnez plus de points")
      actGroup: "POINTS",
    };
  };

  // État du formulaire - utiliser currentActionType
  const [formData, setFormData] = useState(() => createInitialFormData(currentActionType || "YESNO"));

  const imageApiHeaders = useMemo(() => {
    if (isLocalDev && IMAGE_API_TOKEN) {
      return { "x-api-token": IMAGE_API_TOKEN };
    }
    return {};
  }, []);

  const buildImageUrl = useCallback((filename) => {
    return `${getMappedCdnBaseUrl()}/${encodeURIComponent(filename)}`;
  }, []);

  const refreshCdnImages = useCallback(async () => {
    setLoadingCdnImages(true);
    try {
      const response = await fetch(`${IMAGE_SERVICE_URL}/uploads`, {
        headers: imageApiHeaders,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || "Erreur de chargement des images CDN");
      }
      setCdnImages(data.files || []);
    } catch (err) {
      setImageLibraryMessageType("error");
      setImageLibraryMessage(err.message || "Erreur de chargement des images CDN");
    } finally {
      setLoadingCdnImages(false);
    }
  }, [imageApiHeaders]);

  const handleUploadToCdn = async () => {
    if (!cdnUploadFile) return;
    setUploadingToCdn(true);
    setImageLibraryMessageType("info");
    setImageLibraryMessage("Upload en cours...");
    try {
      const bodyFormData = new FormData();
      bodyFormData.append("image", cdnUploadFile);
      if (cdnUploadName.trim()) {
        bodyFormData.append("name", cdnUploadName.trim());
      }

      const response = await fetch(`${IMAGE_SERVICE_URL}/upload`, {
        method: "POST",
        headers: imageApiHeaders,
        body: bodyFormData,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || data.message || "Erreur upload image");
      }

      const uploadedFilename = data.newFilename || data.filename || extractFilenameFromUrl(data.imageUrl);
      const nextUrl = uploadedFilename ? buildImageUrl(uploadedFilename) : data.imageUrl || null;
      if (nextUrl) {
        handleChange(cdnTargetField, nextUrl);
      }
      setCdnUploadFile(null);
      setCdnUploadName("");
      setImageLibraryMessageType("success");
      setImageLibraryMessage("Icône uploadée et bibliothèque mise à jour.");
      await refreshCdnImages();
    } catch (err) {
      setImageLibraryMessageType("error");
      setImageLibraryMessage(err.message || "Erreur upload image");
    } finally {
      setUploadingToCdn(false);
    }
  };

  // Charger les langues disponibles
  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await fetch(`${API_URL}/greenmiles/languages`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const data = await response.json();
        if (data.success === "OK") {
          setAvailableLanguages(data.result || []);
        }
      } catch (err) {
        console.error("Erreur lors du chargement des langues:", err);
      } finally {
        setLoadingLanguages(false);
      }
    };

    if (token) {
      fetchLanguages();
    }
  }, [token]);

  // Charger les données de l'action si on est en mode édition
  useEffect(() => {
    const fetchActionData = async () => {
      if (!actionId || !token) {
        setLoadingData(false);
        return;
      }

      try {
        setLoadingData(true);
        const response = await fetch(`${API_URL}/greenmiles/hotels/${hotelId}/actions/${actionId}`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();
        if (data.success === "OK") {
          const action = data.result;
          const actionTypeInfo = getActionTypeInfo(action.actType);

          // Mettre à jour le type d'action détecté AVANT de préparer les données
          setDetectedActionType(action.actType);

          // Préparer les données du formulaire
          const formattedData = {
            actReward: action.actReward || 5,
            imageUrl: action.actPictureURL || "",
            iconUrl: action.actIconURL || "",
            type: action.actType,
            descriptions: (action.descriptions || []).map((desc) => ({
              language: desc.language,
              name: desc.nameB || desc.name || "",
              nameB: desc.nameB || desc.name || "",
              subtitle: desc.subtitle || "",
              description: desc.description || "",
              ...(actionTypeInfo?.requiresQuestion && action.actType !== "QUIZ" ? { question: desc.question || "" } : {}),
              ...(action.actType === "LINK" ? { question: desc.question || "", linkButtonText: desc.linkButtonText || "" } : {}),
            })),
            actPositiveResponse: action.actPositiveResponse || "YES",
            actSavingsEuro: action.actSavingsEuro || 0,
            actSavingsWater: action.actSavingsWater || 0,
            actSavingsCO2: action.actSavingsCO2 || 0,
            customOptions: (action.customOptions || []).map((option) => ({
              pictureURL: option.caoPictureURL || "",
              descriptions: (option.descriptions || []).map((desc) => ({
                language: desc.language,
                label: desc.label || "",
              })),
            })),
            quizData:
              action.actType === "QUIZ" && action.quizData
                ? {
                    difficulty: action.quizData.difficulty || "EASY",
                    descriptions: (action.quizData.descriptions || []).map((desc) => ({
                      language: desc.language,
                      question: desc.question || "",
                      explanation: desc.explanation || "",
                    })),
                    answers: (action.quizData.answers || []).map((answer) => ({
                      position: answer.position,
                      isCorrect: answer.isCorrect,
                      descriptions: (answer.descriptions || []).map((desc) => ({
                        language: desc.language,
                        text: desc.text || "",
                      })),
                    })),
                  }
                : null,
            actLinkUrl: action.actLinkUrl || "",
            // Config Option Eau (LUNISHOWER)
            actEauConnected: !!action.actEauConnected,
            actEauObjectiveLiters: action.actEauObjectiveLiters ?? 40,
            actEauPointsPerShower: action.actEauPointsPerShower ?? 100,
            // Sous-type de service (SKIP_SERVICE)
            skipSubType: action.skipSubType || "cleaning",
            // Groupe visuel sur l'app invité
            actGroup: action.actGroup || "POINTS",
          };

          // S'assurer qu'il y a au moins une description
          if (formattedData.descriptions.length === 0) {
            formattedData.descriptions = [
              {
                language: "fr",
                name: "",
                nameB: "",
                subtitle: "",
                description: "",
                ...(actionTypeInfo?.requiresQuestion && action.actType !== "QUIZ" ? { question: "" } : {}),
                ...(action.actType === "LINK" ? { question: "", linkButtonText: "" } : {}),
              },
            ];
          }

          setFormData(formattedData);
        } else {
          setErrors({ submit: data.message || "Erreur lors du chargement de l'action" });
        }
      } catch (err) {
        console.error("Erreur lors du chargement de l'action:", err);
        setErrors({ submit: "Erreur lors du chargement de l'action" });
      } finally {
        setLoadingData(false);
      }
    };

    fetchActionData();
  }, [actionId, hotelId, token]); // Retirer typeInfo des dépendances

  useEffect(() => {
    refreshCdnImages();
  }, [refreshCdnImages]);

  const imageFiles = useMemo(() => {
    return cdnImages.filter((filename) => /\.(png|jpe?g|webp|gif|bmp|svg|avif)$/i.test(filename));
  }, [cdnImages]);

  const filteredImageFiles = useMemo(() => {
    if (!cdnImageSearch.trim()) return imageFiles;
    const search = cdnImageSearch.trim().toLowerCase();
    return imageFiles.filter((filename) => filename.toLowerCase().includes(search));
  }, [cdnImageSearch, imageFiles]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  // Gestion des descriptions multilingues
  const handleDescriptionChange = (index, field, value) => {
    const newDescriptions = [...formData.descriptions];
    newDescriptions[index] = {
      ...newDescriptions[index],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      descriptions: newDescriptions,
    }));
  };

  const addDescription = () => {
    // Trouver une langue disponible qui n'est pas déjà utilisée
    const usedLanguages = formData.descriptions.map((d) => d.language);
    const nextLanguage = availableLanguages.find((l) => !usedLanguages.includes(l.language)) || (availableLanguages.length > 0 ? availableLanguages[0] : { language: "en" });

    const newDescription = {
      language: nextLanguage.language || "en",
      name: "",
      nameB: "",
      subtitle: "",
      description: "",
      ...(typeInfo?.requiresQuestion && currentActionType !== "QUIZ" ? { question: "" } : {}),
      ...(currentActionType === "LINK" ? { question: "", linkButtonText: "" } : {}),
    };
    setFormData((prev) => ({
      ...prev,
      descriptions: [...prev.descriptions, newDescription],
    }));
  };

  const removeDescription = (index) => {
    if (formData.descriptions.length > 1) {
      const newDescriptions = formData.descriptions.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        descriptions: newDescriptions,
      }));
    }
  };

  // Gestion des options CUSTOM
  const handleCustomOptionChange = (optionIndex, field, value) => {
    const newOptions = [...formData.customOptions];
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      customOptions: newOptions,
    }));
  };

  const handleCustomOptionDescriptionChange = (optionIndex, descIndex, field, value) => {
    const newOptions = [...formData.customOptions];
    const newDescriptions = [...newOptions[optionIndex].descriptions];
    newDescriptions[descIndex] = {
      ...newDescriptions[descIndex],
      [field]: value,
    };
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      descriptions: newDescriptions,
    };
    setFormData((prev) => ({
      ...prev,
      customOptions: newOptions,
    }));
  };

  const addCustomOption = () => {
    const newOption = {
      pictureURL: "",
      descriptions: [
        { language: "fr", label: "" },
        { language: "en", label: "" },
      ],
    };
    setFormData((prev) => ({
      ...prev,
      customOptions: [...prev.customOptions, newOption],
    }));
  };

  const removeCustomOption = (index) => {
    if (formData.customOptions.length > 2) {
      const newOptions = formData.customOptions.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        customOptions: newOptions,
      }));
    }
  };

  const addCustomOptionDescription = (optionIndex) => {
    const newOptions = [...formData.customOptions];
    const newDescription = {
      language: availableLanguages.find((l) => !newOptions[optionIndex].descriptions.some((d) => d.language === l.language))?.language || "en",
      label: "",
    };
    newOptions[optionIndex] = {
      ...newOptions[optionIndex],
      descriptions: [...newOptions[optionIndex].descriptions, newDescription],
    };
    setFormData((prev) => ({
      ...prev,
      customOptions: newOptions,
    }));
  };

  const removeCustomOptionDescription = (optionIndex, descIndex) => {
    const newOptions = [...formData.customOptions];
    if (newOptions[optionIndex].descriptions.length > 1) {
      newOptions[optionIndex] = {
        ...newOptions[optionIndex],
        descriptions: newOptions[optionIndex].descriptions.filter((_, i) => i !== descIndex),
      };
      setFormData((prev) => ({
        ...prev,
        customOptions: newOptions,
      }));
    }
  };

  // Gestion du quiz
  const handleQuizDifficultyChange = (difficulty) => {
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        difficulty,
      },
    }));
  };

  const handleQuizDescriptionChange = (index, field, value) => {
    const newDescriptions = [...formData.quizData.descriptions];
    newDescriptions[index] = {
      ...newDescriptions[index],
      [field]: value,
    };
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        descriptions: newDescriptions,
      },
    }));
  };

  const addQuizDescription = () => {
    const usedLanguages = formData.quizData.descriptions.map((d) => d.language);
    const nextLanguage = availableLanguages.find((l) => !usedLanguages.includes(l.language)) || (availableLanguages.length > 0 ? availableLanguages[0] : { language: "en" });

    const newDescription = {
      language: nextLanguage.language || "en",
      question: "",
      explanation: "",
    };
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        descriptions: [...prev.quizData.descriptions, newDescription],
      },
    }));
  };

  const removeQuizDescription = (index) => {
    if (formData.quizData.descriptions.length > 1) {
      const newDescriptions = formData.quizData.descriptions.filter((_, i) => i !== index);
      setFormData((prev) => ({
        ...prev,
        quizData: {
          ...prev.quizData,
          descriptions: newDescriptions,
        },
      }));
    }
  };

  const handleQuizAnswerChange = (answerIndex, descIndex, value) => {
    const newAnswers = [...formData.quizData.answers];
    const newDescriptions = [...newAnswers[answerIndex].descriptions];
    newDescriptions[descIndex] = {
      ...newDescriptions[descIndex],
      text: value,
    };
    newAnswers[answerIndex] = {
      ...newAnswers[answerIndex],
      descriptions: newDescriptions,
    };
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        answers: newAnswers,
      },
    }));
  };

  const addQuizAnswerDescription = (answerIndex) => {
    const newAnswers = [...formData.quizData.answers];
    const usedLanguages = newAnswers[answerIndex].descriptions.map((d) => d.language);
    const nextLanguage = availableLanguages.find((l) => !usedLanguages.includes(l.language)) || (availableLanguages.length > 0 ? availableLanguages[0] : { language: "en" });

    const newDescription = {
      language: nextLanguage.language || "en",
      text: "",
    };
    newAnswers[answerIndex] = {
      ...newAnswers[answerIndex],
      descriptions: [...newAnswers[answerIndex].descriptions, newDescription],
    };
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        answers: newAnswers,
      },
    }));
  };

  const removeQuizAnswerDescription = (answerIndex, descIndex) => {
    const newAnswers = [...formData.quizData.answers];
    if (newAnswers[answerIndex].descriptions.length > 1) {
      newAnswers[answerIndex] = {
        ...newAnswers[answerIndex],
        descriptions: newAnswers[answerIndex].descriptions.filter((_, i) => i !== descIndex),
      };
      setFormData((prev) => ({
        ...prev,
        quizData: {
          ...prev.quizData,
          answers: newAnswers,
        },
      }));
    }
  };

  const setCorrectAnswer = (index) => {
    const newAnswers = formData.quizData.answers.map((answer, i) => ({
      ...answer,
      isCorrect: i === index ? (answer.isCorrect === 1 ? 0 : 1) : answer.isCorrect,
    }));
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        answers: newAnswers,
      },
    }));
  };

  const addQuizAnswer = () => {
    const newAnswer = {
      position: formData.quizData.answers.length + 1,
      isCorrect: 0,
      descriptions: [
        { language: "fr", text: "" },
        { language: "en", text: "" },
      ],
    };
    setFormData((prev) => ({
      ...prev,
      quizData: {
        ...prev.quizData,
        answers: [...prev.quizData.answers, newAnswer],
      },
    }));
  };

  const removeQuizAnswer = (index) => {
    if (formData.quizData.answers.length > 2) {
      const newAnswers = formData.quizData.answers.filter((_, i) => i !== index).map((answer, i) => ({ ...answer, position: i + 1 }));
      setFormData((prev) => ({
        ...prev,
        quizData: {
          ...prev.quizData,
          answers: newAnswers,
        },
      }));
    }
  };

  const validateForm = () => {
    const newErrors = validateActionFormData(formData, currentActionType);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      // Le titre unique de l'UI alimente name et nameB pour rester compatible avec les consommateurs existants.
      const frenchDesc = formData.descriptions.find((d) => d.language === "fr");
      const actName = frenchDesc?.name?.trim() || "";

      // Préparer le payload selon le type
      const payload = {
        actName,
        actType: formData.type || currentActionType,
        actReward: Number(formData.actReward),
        actPictureURL: null,
        actIconURL: formData.iconUrl.trim() || null,
        actTitleColor: "#FFFFFF",
        actSavingsEuro: Number(formData.actSavingsEuro) || 0,
        actSavingsWater: Number(formData.actSavingsWater) || 0,
        actSavingsCO2: Number(formData.actSavingsCO2) || 0,
        descriptions: formData.descriptions.map((desc) => ({
          language: desc.language,
          name: desc.name.trim(),
          nameB: desc.name.trim(),
          subtitle: desc.subtitle?.trim() || "",
          description: desc.description.trim(),
          ...(typeInfo?.requiresQuestion && formData.type !== "QUIZ" ? { question: desc.question?.trim() || "" } : {}),
          ...(formData.type === "LINK" ? { question: desc.question?.trim() || "", linkButtonText: desc.linkButtonText?.trim() || "" } : {}),
        })),
      };

      // Ajouter la réponse positive si nécessaire
      if (typeInfo?.requiresPositiveResponse) {
        payload.actPositiveResponse = formData.actPositiveResponse;
      }

      // Ajouter l'URL du lien pour LINK
      if (formData.type === "LINK") {
        payload.actLinkUrl = formData.actLinkUrl.trim();
      }

      // Config Option Eau pour LUNISHOWER
      if (formData.type === "LUNISHOWER") {
        payload.actEauConnected = formData.actEauConnected ? 1 : 0;
        payload.actEauObjectiveLiters = Number(formData.actEauObjectiveLiters) || 40;
        payload.actEauPointsPerShower = Number(formData.actEauPointsPerShower) || 100;
      }

      // Sous-type de service pour SKIP_SERVICE
      if (formData.type === "SKIP_SERVICE") {
        payload.skipSubType = formData.skipSubType || "cleaning";
      }

      // Groupe visuel sur l'app invité (tous types ; POINTS = section par défaut)
      payload.actGroup = formData.actGroup || "POINTS";

      // Ajouter les options CUSTOM
      if (formData.type === "CUSTOM") {
        payload.customOptions = formData.customOptions.map((option) => ({
          pictureURL: option.pictureURL?.trim() || null,
          descriptions: option.descriptions.map((desc) => ({
            language: desc.language,
            label: desc.label.trim(),
          })),
        }));
      }

      // Ajouter les données du quiz
      if (formData.type === "QUIZ") {
        payload.quizData = {
          difficulty: formData.quizData.difficulty,
          descriptions: formData.quizData.descriptions.map((desc) => ({
            language: desc.language,
            question: desc.question.trim(),
            explanation: desc.explanation?.trim() || "",
          })),
          answers: formData.quizData.answers.map((answer) => ({
            position: answer.position,
            isCorrect: answer.isCorrect,
            descriptions: answer.descriptions.map((desc) => ({
              language: desc.language,
              text: desc.text.trim(),
            })),
          })),
        };
      }

      const url = isEditMode ? `${API_URL}/greenmiles/hotels/${hotelId}/actions/${actionId}` : `${API_URL}/greenmiles/hotels/${hotelId}/actions`;

      const response = await fetch(url, {
        method: isEditMode ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success === "OK") {
        onSuccess?.();
      } else {
        setErrors({
          submit: data.message || (isEditMode ? "Erreur lors de la modification de l'action" : "Erreur lors de la création de l'action"),
        });
      }
    } catch (err) {
      console.error(`Erreur lors de ${isEditMode ? "la modification" : "la création"}:`, err);
      setErrors({
        submit: isEditMode ? "Erreur lors de la modification de l'action" : "Erreur lors de la création de l'action",
      });
    } finally {
      setLoading(false);
    }
  };

  // Ne pas afficher le formulaire tant que les langues ne sont pas chargées
  // En mode édition, attendre aussi que les données soient chargées
  if (loadingLanguages || (isEditMode && loadingData)) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* En-tête avec info sur le type */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <h3 className="text-lg font-semibold text-green-800 mb-2">📋 {typeInfo?.displayName || formData.type || currentActionType}</h3>
        <p className="text-sm text-green-700">{typeInfo?.description || ""}</p>
      </div>

      {/* Erreur générale */}
      {errors.submit && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-sm text-red-800">{errors.submit}</p>
        </div>
      )}

      {/* Descriptions multilingues */}
      <div className="border-t border-gray-200 pt-4">
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-sm font-medium text-gray-700">Descriptions multilingues</h4>
          <button type="button" onClick={addDescription} className="text-sm text-green-600 hover:text-green-700 font-medium">
            + Ajouter une langue
          </button>
        </div>

        <div className="space-y-4">
          {formData.descriptions.map((desc, index) => (
            <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <div className="flex justify-between items-center mb-3">
                <h5 className="text-sm font-medium text-gray-800">
                  Description {index + 1} - {getLanguageName(desc.language)}
                </h5>
                {formData.descriptions.length > 1 && (
                  <button type="button" onClick={() => removeDescription(index)} className="text-xs text-red-600 hover:text-red-800">
                    Supprimer
                  </button>
                )}
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Langue <span className="text-red-500">*</span>
                  </label>
                  <select value={desc.language} onChange={(e) => handleDescriptionChange(index, "language", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                    {availableLanguages.map((lang) => (
                      <option key={lang.language} value={lang.language}>
                        {lang.name || getLanguageName(lang.language)}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Titre <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={desc.name} onChange={(e) => handleDescriptionChange(index, "name", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Ex: Une douche plus courte ?" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Sous-titre de la card <span className="text-red-500">*</span>
                  </label>
                  <input type="text" value={desc.subtitle || ""} onChange={(e) => handleDescriptionChange(index, "subtitle", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Ex: Réduisez votre empreinte carbone" />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea value={desc.description} onChange={(e) => handleDescriptionChange(index, "description", e.target.value)} rows={3} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Décrivez l'action écologique..." />
                </div>

                {/* Question si nécessaire */}
                {typeInfo?.requiresQuestion && actionType !== "QUIZ" && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                      Question <span className="text-red-500">*</span>
                    </label>
                    <textarea value={desc.question || ""} onChange={(e) => handleDescriptionChange(index, "question", e.target.value)} rows={2} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Ex: Avez-vous éteint les lumières ?" />
                  </div>
                )}

                {/* Champs spécifiques LINK */}
                {formData.type === "LINK" && (
                  <div className="space-y-3 pt-3 border-t border-gray-200">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Question de vérification <span className="text-red-500">*</span>
                      </label>
                      <textarea value={desc.question || ""} onChange={(e) => handleDescriptionChange(index, "question", e.target.value)} rows={2} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Ex: Avez-vous bien posté ?" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">Texte du bouton</label>
                      <input type="text" value={desc.linkButtonText || ""} onChange={(e) => handleDescriptionChange(index, "linkButtonText", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Ex: Poster" />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {errors.descriptions && <p className="text-xs text-red-500 mt-1">{errors.descriptions}</p>}
        {errors.question && <p className="text-xs text-red-500 mt-1">{errors.question}</p>}
      </div>

      {/* Réponse positive (pour YESNO, YESNOSTAY, YESNOMAIL) */}
      {typeInfo?.requiresPositiveResponse && (
        <div className="border-t border-gray-200 pt-4">
          <label htmlFor="actPositiveResponse" className="block text-sm font-medium text-gray-700 mb-1">
            Réponse positive <span className="text-red-500">*</span>
          </label>
          <select id="actPositiveResponse" value={formData.actPositiveResponse} onChange={(e) => handleChange("actPositiveResponse", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
            <option value="YES">Oui</option>
            <option value="NO">Non</option>
          </select>
          {errors.actPositiveResponse && <p className="text-xs text-red-500 mt-1">{errors.actPositiveResponse}</p>}
        </div>
      )}

      {/* URL du lien (pour LINK) */}
      {formData.type === "LINK" && (
        <div className="border-t border-gray-200 pt-4">
          <label htmlFor="actLinkUrl" className="block text-sm font-medium text-gray-700 mb-1">
            URL du lien <span className="text-red-500">*</span>
          </label>
          <input type="url" id="actLinkUrl" value={formData.actLinkUrl} onChange={(e) => handleChange("actLinkUrl", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.actLinkUrl ? "border-red-500" : "border-gray-300"}`} placeholder="https://www.example.com" />
          {errors.actLinkUrl && <p className="text-xs text-red-500 mt-1">{errors.actLinkUrl}</p>}
        </div>
      )}

      {/* Options personnalisées (pour CUSTOM) */}
      {formData.type === "CUSTOM" && (
        <div className="border-t border-gray-200 pt-4">
          <div className="flex justify-between items-center mb-4">
            <h4 className="text-sm font-medium text-gray-700">Options personnalisées</h4>
            <button type="button" onClick={addCustomOption} className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md">
              + Ajouter une option
            </button>
          </div>

          {formData.customOptions.length < 2 && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-4">
              <p className="text-sm text-orange-800">⚠️ Au moins 2 options sont nécessaires pour les actions CUSTOM</p>
            </div>
          )}

          <div className="space-y-4">
            {formData.customOptions.map((option, optionIndex) => (
              <div key={optionIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex justify-between items-center mb-3">
                  <h5 className="text-sm font-medium text-gray-800">Option {optionIndex + 1}</h5>
                  {formData.customOptions.length > 2 && (
                    <button type="button" onClick={() => removeCustomOption(optionIndex)} className="text-xs text-red-600 hover:text-red-800">
                      Supprimer
                    </button>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">URL de l'image (optionnel)</label>
                    <input type="url" value={option.pictureURL || ""} onChange={(e) => handleCustomOptionChange(optionIndex, "pictureURL", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="https://..." />
                  </div>

                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <label className="block text-xs font-medium text-gray-600">Libellés</label>
                      <button type="button" onClick={() => addCustomOptionDescription(optionIndex)} className="text-xs text-green-600 hover:text-green-700">
                        + Ajouter une langue
                      </button>
                    </div>

                    <div className="space-y-2">
                      {option.descriptions.map((desc, descIndex) => (
                        <div key={descIndex} className="flex gap-2">
                          <select value={desc.language} onChange={(e) => handleCustomOptionDescriptionChange(optionIndex, descIndex, "language", e.target.value)} className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                            {availableLanguages.map((lang) => (
                              <option key={lang.language} value={lang.language}>
                                {lang.name || getLanguageName(lang.language)}
                              </option>
                            ))}
                          </select>
                          <input type="text" value={desc.label} onChange={(e) => handleCustomOptionDescriptionChange(optionIndex, descIndex, "label", e.target.value)} className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Libellé de l'option" />
                          {option.descriptions.length > 1 && (
                            <button type="button" onClick={() => removeCustomOptionDescription(optionIndex, descIndex)} className="text-red-600 hover:text-red-800 px-2">
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {errors.customOptions && <p className="text-xs text-red-500 mt-1">{errors.customOptions}</p>}
        </div>
      )}

      {/* Quiz (pour QUIZ) */}
      {formData.type === "QUIZ" && formData.quizData && (
        <div className="border-t border-gray-200 pt-4 space-y-6">
          <h4 className="text-sm font-medium text-gray-700">Configuration du quiz</h4>

          {/* Difficulté */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Niveau de difficulté <span className="text-red-500">*</span>
            </label>
            <div className="flex gap-2">
              {["EASY", "MEDIUM", "HARD"].map((difficulty) => (
                <button key={difficulty} type="button" onClick={() => handleQuizDifficultyChange(difficulty)} className={`px-4 py-2 rounded-md text-sm font-medium ${formData.quizData.difficulty === difficulty ? "bg-green-600 text-white" : "bg-gray-100 text-gray-700 hover:bg-gray-200"}`}>
                  {difficulty}
                </button>
              ))}
            </div>
          </div>

          {/* Descriptions de la question */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Question du quiz</label>
              <button type="button" onClick={addQuizDescription} className="text-sm text-green-600 hover:text-green-700">
                + Ajouter une langue
              </button>
            </div>

            <div className="space-y-3">
              {formData.quizData.descriptions.map((desc, index) => (
                <div key={index} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-xs font-medium text-gray-600">{getLanguageName(desc.language)}</span>
                    {formData.quizData.descriptions.length > 1 && (
                      <button type="button" onClick={() => removeQuizDescription(index)} className="text-xs text-red-600 hover:text-red-800">
                        Supprimer
                      </button>
                    )}
                  </div>
                  <div className="space-y-2">
                    <select value={desc.language} onChange={(e) => handleQuizDescriptionChange(index, "language", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                      {availableLanguages.map((lang) => (
                        <option key={lang.language} value={lang.language}>
                          {lang.name || getLanguageName(lang.language)}
                        </option>
                      ))}
                    </select>
                    <input type="text" value={desc.question || ""} onChange={(e) => handleQuizDescriptionChange(index, "question", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Question" />
                    <textarea value={desc.explanation || ""} onChange={(e) => handleQuizDescriptionChange(index, "explanation", e.target.value)} rows={2} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Explication" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Réponses */}
          <div>
            <div className="flex justify-between items-center mb-3">
              <label className="block text-sm font-medium text-gray-700">Réponses</label>
              <button type="button" onClick={addQuizAnswer} className="px-3 py-1 text-sm bg-green-600 hover:bg-green-700 text-white rounded-md">
                + Ajouter une réponse
              </button>
            </div>

            <div className="space-y-4">
              {formData.quizData.answers.map((answer, answerIndex) => (
                <div key={answerIndex} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-3">
                    <h5 className="text-sm font-medium text-gray-800">Réponse {answer.position}</h5>
                    <div className="flex gap-2">
                      <button type="button" onClick={() => setCorrectAnswer(answerIndex)} className={`px-3 py-1 rounded text-xs font-medium ${answer.isCorrect === 1 ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
                        {answer.isCorrect === 1 ? "✓ Correcte" : "Marquer correcte"}
                      </button>
                      {formData.quizData.answers.length > 2 && (
                        <button type="button" onClick={() => removeQuizAnswer(answerIndex)} className="text-xs text-red-600 hover:text-red-800">
                          Supprimer
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {answer.descriptions.map((desc, descIndex) => (
                      <div key={descIndex} className="flex gap-2">
                        <select
                          value={desc.language}
                          onChange={(e) => {
                            const newDescriptions = [...answer.descriptions];
                            newDescriptions[descIndex] = { ...desc, language: e.target.value };
                            const newAnswers = [...formData.quizData.answers];
                            newAnswers[answerIndex] = { ...answer, descriptions: newDescriptions };
                            setFormData((prev) => ({
                              ...prev,
                              quizData: {
                                ...prev.quizData,
                                answers: newAnswers,
                              },
                            }));
                          }}
                          className="w-32 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                        >
                          {availableLanguages.map((lang) => (
                            <option key={lang.language} value={lang.language}>
                              {lang.name || getLanguageName(lang.language)}
                            </option>
                          ))}
                        </select>
                        <input type="text" value={desc.text} onChange={(e) => handleQuizAnswerChange(answerIndex, descIndex, e.target.value)} className="flex-1 p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" placeholder="Texte de la réponse" />
                        {answer.descriptions.length > 1 && (
                          <button type="button" onClick={() => removeQuizAnswerDescription(answerIndex, descIndex)} className="text-red-600 hover:text-red-800 px-2">
                            ×
                          </button>
                        )}
                        <button type="button" onClick={() => addQuizAnswerDescription(answerIndex)} className="text-xs text-green-600 hover:text-green-700 px-2">
                          + Langue
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {errors.quizData && <p className="text-xs text-red-500 mt-1">{errors.quizData}</p>}
          {errors.quizDescriptions && <p className="text-xs text-red-500 mt-1">{errors.quizDescriptions}</p>}
          {errors.quizAnswers && <p className="text-xs text-red-500 mt-1">{errors.quizAnswers}</p>}
        </div>
      )}

      {/* Config sous-type de service (SKIP_SERVICE) */}
      {(formData.type === "SKIP_SERVICE" || currentActionType === "SKIP_SERVICE") && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Configuration Skip Service</h4>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
            <div>
              <label htmlFor="skipSubType" className="block text-sm font-medium text-gray-700 mb-1">
                Service décliné
              </label>
              <select id="skipSubType" value={formData.skipSubType || "cleaning"} onChange={(e) => handleChange("skipSubType", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                <option value="cleaning">Ménage</option>
                <option value="sheets">Draps</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Le guest choisit une récurrence (1 jour sur 2/3/4) : la récompense est créditée par nuit sans service. Le sous-titre côté app est construit à partir de ce sous-type.</p>
            </div>
          </div>
        </div>
      )}

      {/* Config Option Eau (LUNISHOWER) */}
      {(formData.type === "LUNISHOWER" || currentActionType === "LUNISHOWER") && (
        <div className="border-t border-gray-200 pt-4">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Configuration Option Eau</h4>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
            <div>
              <label htmlFor="actEauConnected" className="block text-sm font-medium text-gray-700 mb-1">
                Connectivité Option Eau
              </label>
              <select id="actEauConnected" value={formData.actEauConnected ? "1" : "0"} onChange={(e) => handleChange("actEauConnected", e.target.value === "1")} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
                <option value="0">Non</option>
                <option value="1">Oui</option>
              </select>
            </div>

            <div>
              <label htmlFor="actEauObjectiveLiters" className="block text-sm font-medium text-gray-700 mb-1">
                Objectif de consommation (litres)
              </label>
              <input
                type="number"
                id="actEauObjectiveLiters"
                inputMode="numeric"
                min="1"
                max="200"
                value={formData.actEauObjectiveLiters ?? 40}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  handleChange("actEauObjectiveLiters", Number.isNaN(v) ? 40 : v);
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
              />
            </div>

            <div>
              <label htmlFor="actEauPointsPerShower" className="block text-sm font-medium text-gray-700 mb-1">
                Points par douche qualifiante
              </label>
              <input
                type="text"
                id="actEauPointsPerShower"
                value={formData.actEauPointsPerShower ?? 100}
                onChange={(e) => handleChange("actEauPointsPerShower", e.target.value)}
                onBlur={(e) => {
                  const v = parseInt(e.target.value, 10);
                  handleChange("actEauPointsPerShower", !Number.isNaN(v) && v > 0 ? v : 100);
                }}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500"
                placeholder="100"
              />
            </div>
          </div>
        </div>
      )}

      {/* Informations complémentaires */}
      <div className="border-t border-gray-200 pt-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Informations complémentaires</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="rewardPoints" className="block text-sm font-medium text-gray-700 mb-1">
              Récompense (GreenMiles) <span className="text-red-500">*</span>
            </label>
            <input type="number" id="rewardPoints" min="0" max="1000" value={formData.actReward} onChange={(e) => handleChange("actReward", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.actReward ? "border-red-500" : "border-gray-300"}`} />
            {errors.actReward && <p className="text-xs text-red-500 mt-1">{errors.actReward}</p>}
          </div>

          <div>
            <label htmlFor="actGroup" className="block text-sm font-medium text-gray-700 mb-1">
              Groupe d'affichage
            </label>
            <select id="actGroup" value={formData.actGroup || "POINTS"} onChange={(e) => handleChange("actGroup", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500">
              <option value="POINTS">Gagner plus de points (défaut)</option>
              <option value="WATER">Économisez de l'eau</option>
              <option value="ENVIRONMENT">Réduisez vos émissions</option>
              <option value="ENGAGEMENT">Découvrez nos engagements</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label htmlFor="iconUrl" className="block text-sm font-medium text-gray-700 mb-1">
              URL de l'icône <span className="text-red-500">*</span>
            </label>
            <input type="url" id="iconUrl" value={formData.iconUrl} onChange={(e) => handleChange("iconUrl", e.target.value)} className={`w-full p-2 border rounded-md focus:ring-2 focus:ring-green-500 ${errors.iconUrl ? "border-red-500" : "border-gray-300"}`} placeholder="https://..." />
            {formData.iconUrl && (
              <div className="mt-2">
                <img
                  src={formData.iconUrl}
                  alt="Aperçu icône"
                  className="w-24 h-24 object-contain rounded-lg border border-gray-200 bg-white p-1"
                  onError={(e) => {
                    e.target.style.display = "none";
                  }}
                />
              </div>
            )}
            {errors.iconUrl && <p className="text-xs text-red-500 mt-1">{errors.iconUrl}</p>}
          </div>
        </div>

        <div className="mt-4 border border-gray-200 rounded-md p-4 bg-gray-50 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm font-medium text-gray-700">CDN d'images</p>
            <button type="button" onClick={refreshCdnImages} className="text-xs underline text-[#365644] whitespace-nowrap">
              Rafraîchir
            </button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1fr,1fr,auto] gap-2">
            <input type="file" accept="image/*" onChange={(e) => setCdnUploadFile(e.target.files?.[0] || null)} className="block w-full text-xs text-gray-700" />
            <input type="text" value={cdnUploadName} onChange={(e) => setCdnUploadName(e.target.value)} placeholder="Nom personnalisé (optionnel)" className="w-full p-2 border border-gray-300 rounded-md text-sm" />
            <button type="button" onClick={handleUploadToCdn} disabled={!cdnUploadFile || uploadingToCdn} className="px-4 py-2 text-sm bg-[#365644] text-white rounded-md hover:bg-[#2d4c3c] disabled:opacity-60 disabled:cursor-not-allowed">
              {uploadingToCdn ? "Upload..." : "Uploader"}
            </button>
          </div>

          <div className="flex items-center gap-2">
            <input type="text" value={cdnImageSearch} onChange={(e) => setCdnImageSearch(e.target.value)} placeholder="Rechercher une image du CDN..." className="w-full p-2 border border-gray-300 rounded-md text-sm" />
            <span className="text-xs text-gray-500 whitespace-nowrap">{filteredImageFiles.length} résultat(s)</span>
          </div>

          {imageLibraryMessage && <p className={`text-xs ${imageLibraryMessageType === "error" ? "text-red-600" : imageLibraryMessageType === "success" ? "text-green-700" : "text-gray-600"}`}>{imageLibraryMessage}</p>}

          {loadingCdnImages ? (
            <p className="text-xs text-gray-500">Chargement des images...</p>
          ) : filteredImageFiles.length === 0 ? (
            <p className="text-xs text-gray-500">Aucune image disponible.</p>
          ) : (
            <div className="max-h-64 overflow-y-auto pr-1">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {filteredImageFiles.map((filename) => {
                  const imageUrl = buildImageUrl(filename);
                  const isSelected = formData[cdnTargetField] === imageUrl;
                  return (
                    <button
                      key={filename}
                      type="button"
                      onClick={() => {
                        handleChange(cdnTargetField, imageUrl);
                        setImageLibraryMessageType("success");
                        setImageLibraryMessage("Icône sélectionnée.");
                      }}
                      className={`text-left border rounded-md overflow-hidden bg-white hover:border-[#365644] ${isSelected ? "border-[#365644] ring-1 ring-[#365644]" : "border-gray-200"}`}
                    >
                      <img src={imageUrl} alt={filename} className="w-full h-20 object-contain bg-white p-1" loading="lazy" />
                      <p className="px-2 py-1 text-[11px] text-gray-700 truncate">{filename}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Économies réalisées */}
        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">Économies réalisées</label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label htmlFor="actSavingsEuro" className="block text-xs font-medium text-gray-600 mb-1">
                Euros (€)
              </label>
              <input type="number" id="actSavingsEuro" min="0" step="0.01" value={formData.actSavingsEuro} onChange={(e) => handleChange("actSavingsEuro", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label htmlFor="actSavingsWater" className="block text-xs font-medium text-gray-600 mb-1">
                Eau (L)
              </label>
              <input type="number" id="actSavingsWater" min="0" step="0.01" value={formData.actSavingsWater} onChange={(e) => handleChange("actSavingsWater", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" />
            </div>
            <div>
              <label htmlFor="actSavingsCO2" className="block text-xs font-medium text-gray-600 mb-1">
                CO₂ (kg)
              </label>
              <input type="number" id="actSavingsCO2" min="0" step="0.01" value={formData.actSavingsCO2} onChange={(e) => handleChange("actSavingsCO2", e.target.value)} className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-green-500" />
            </div>
          </div>
        </div>
      </div>

      {/* Boutons */}
      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
        <button type="button" onClick={onCancel} className="px-4 py-2 text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors">
          Annuler
        </button>
        <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
          {loading ? (isEditMode ? "Modification..." : "Création...") : isEditMode ? "Modifier l'action" : "Créer l'action"}
        </button>
      </div>
    </form>
  );
}

export default SimpleActionFormV2;
